import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { RootState } from "./store"
import type { AgentStep } from "@/lib/agentStepParser"
import { rttNew } from "./rttSlice"
import { turnAnswered } from "./agentTurnActions"

export type PipelineStep = AgentStep & { timestamp: number }

export type AgentChatState = "composing" | "active" | "paused"

export type AgentTurn = {
  startedAt: number
  steps: PipelineStep[]
  // What the agent last said it is doing, from this turn's composing only, so
  // a new turn never opens with the previous one's text.
  activity?: string
  // A message sent while the agent is still busy starts the next turn the
  // moment this one is answered, timed from when the dispatcher sent it.
  nextStartedAt?: number
}

export type FinishedTurn = { durationMs: number; steps: PipelineStep[] }

export type AgentPipelineState = {
  live: Record<string, AgentTurn | undefined>
  // Kept for the life of the tab and gone on reload: steps are not archived
  // in MAM. A few small entries per answered turn, so nothing evicts them.
  finished: Record<string, FinishedTurn | undefined>
}

/**
 * Anything the agent sends about its work, apart from the answer itself:
 * a chat state, optionally with a step and an activity line.
 */
export type AgentSignal = {
  roomJid: string
  at: number
  chatState: AgentChatState
  activity?: string
  step?: AgentStep
}

const initialState: AgentPipelineState = { live: {}, finished: {} }

// Two agents running in parallel can share a display name, so a step is only
// the same step when the agent says so with a step id. Without one (older
// agents) we fall back to name and type, which keeps a single row per name.
const matchesStep = (a: PipelineStep, b: PipelineStep): boolean =>
  a.stepId !== undefined || b.stepId !== undefined
    ? a.stepId === b.stepId
    : a.name === b.name && a.type === b.type

const upsertStep = (steps: PipelineStep[], step: PipelineStep) => {
  const existingIdx = steps.findIndex((s) => matchesStep(s, step))
  if (existingIdx >= 0) {
    steps[existingIdx] = step
    return
  }
  // A stanza can be lost in flight, so a done may name an id we never saw
  // start. Opening a row for a step that is already over would show the
  // dispatcher a tick for work they never saw running.
  if (step.stepId !== undefined && step.status === "done") return
  steps.push(step)
}

const openTurn = (state: AgentPipelineState, roomJid: string, at: number) =>
  (state.live[roomJid] = { startedAt: at, steps: [] })

const agentPipelineSlice = createSlice({
  name: "agentPipeline",
  initialState,
  reducers: {
    turnSent: (
      state,
      action: PayloadAction<{ roomJid: string; at: number }>,
    ) => {
      const { roomJid, at } = action.payload
      const turn = state.live[roomJid]
      if (!turn) {
        openTurn(state, roomJid, at)
      } else if (turn.nextStartedAt === undefined) {
        turn.nextStartedAt = at
      }
    },
    // The whole turn-boundary table for the agent's side lives here, so no
    // caller can open or close a turn by dispatching in the wrong order.
    agentSignal: (state, action: PayloadAction<AgentSignal>) => {
      const { roomJid, at, chatState, activity, step } = action.payload
      // Active or paused with no body: the turn ended without an answer
      // (empty reply or an error), so there is nothing to sum up.
      if (chatState !== "composing") {
        delete state.live[roomJid]
        return
      }
      let turn = state.live[roomJid]
      if (!turn) {
        // A done with no turn open is a late tick after the answer closed it.
        // Reopening would leave "W toku" running with nothing to end it.
        if (step?.status === "done") return
        // Otherwise the agent is working on a turn this tab never sent: a
        // cron, another tab, or a reload mid-turn.
        turn = openTurn(state, roomJid, at)
      }
      if (activity !== undefined) turn.activity = activity
      if (step) upsertStep(turn.steps, { ...step, timestamp: at })
    },
    // Nothing has said the agent is working for too long: it crashed or the
    // answer was lost. Without this "W toku" would count until a reload.
    turnExpired: (state, action: PayloadAction<{ roomJid: string }>) => {
      delete state.live[action.payload.roomJid]
    },
  },
  extraReducers: (builder) => {
    // Text streaming is the agent working too. After a reload mid-answer it
    // is the first thing that says so.
    builder.addCase(rttNew, (state, action) => {
      const { roomJid, at } = action.payload
      if (!state.live[roomJid]) openTurn(state, roomJid, at)
    })
    builder.addCase(turnAnswered, (state, action) => {
      const { roomJid, messageId, at } = action.payload
      const turn = state.live[roomJid]
      if (!turn) return
      if (turn.steps.length > 0) {
        state.finished[messageId] = {
          durationMs: at - turn.startedAt,
          // Once the answer is in, every step is over, including a
          // delegation whose done never arrived before the body.
          steps: turn.steps.map((step) => ({ ...step, status: "done" })),
        }
      }
      state.live[roomJid] =
        turn.nextStartedAt === undefined
          ? undefined
          : { startedAt: turn.nextStartedAt, steps: [] }
    })
  },
})

export const { turnSent, agentSignal, turnExpired } = agentPipelineSlice.actions
export default agentPipelineSlice.reducer

export const selectLiveTurn = (
  state: RootState,
  roomJid: string,
): AgentTurn | undefined => state.agentPipeline.live[roomJid]

export const selectFinishedTurn = (
  state: RootState,
  messageId: string,
): FinishedTurn | undefined => state.agentPipeline.finished[messageId]

export const selectFinishedTurns = (
  state: RootState,
): AgentPipelineState["finished"] => state.agentPipeline.finished
